import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { savedSearchFiltersQueryKeys } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/query";
import type { SavedSearchRoutingParams } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types";
import { ESavedSearchActions } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types/enums";
import { selectSavedSearchFiltersCount } from "app/pages/sales-intelligence/modules/leads-search-filters";
import trackingService from "app/pages/sales-intelligence/sub-modules/advanced-search/services/tracking/advancedSearchTrackingService";
import type { SavedSearchDto } from "app/pages/sales-intelligence/sub-modules/advanced-search/types/dto";

import SavedSearchActionsDropdown from "../../components/SavedSearchActionsDropdown/SavedSearchActionsDropdown";
import StartNewSearchButton from "../../components/StartNewSearchButton/StartNewSearchButton";
import { useSavedSearchActions } from "../../hooks/useSavedSearchActions";

import { StyledActionsContainer } from "./styles";

const UNKNOWN_SEARCH_TEXT = "Unknown saved search";

type Props = {
    isUpdateEnabled?: boolean;
};

const SavedSearchActionsContainer = ({ isUpdateEnabled }: Props) => {
    const queryClient = useQueryClient();

    const {
        params: { savedSearchId },
    } = useCurrentStateAndParams<SavedSearchRoutingParams>();

    const savedSearchFiltersCount = useSelector(selectSavedSearchFiltersCount);

    const { onSaveSearch, onStartNewSearch } = useSavedSearchActions(savedSearchId ?? "");

    const handleOnSaveSearchTracked = (action: ESavedSearchActions): void => {
        const savedSearchData = queryClient.getQueryData<SavedSearchDto>(
            savedSearchFiltersQueryKeys.getById(savedSearchId),
        );

        if (action === ESavedSearchActions.Save) {
            trackingService.filtersPanel.trackSaveAsNewSearchClicked(
                savedSearchFiltersCount,
                savedSearchData?.totalNumberOfWebsites ?? 0,
            );
        } else {
            trackingService.filtersPanel.trackUpdateSearchClicked(
                savedSearchData?.name ?? UNKNOWN_SEARCH_TEXT,
                savedSearchFiltersCount,
            );
        }

        onSaveSearch(action, savedSearchData);
    };

    return (
        <StyledActionsContainer>
            <StartNewSearchButton onClick={onStartNewSearch} />
            <SavedSearchActionsDropdown
                // TODO Add verification, the filters are dirty
                isUpdateEnabled={isUpdateEnabled}
                onSelect={handleOnSaveSearchTracked}
            />
        </StyledActionsContainer>
    );
};

export default SavedSearchActionsContainer;
